import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { requestAudienceMagnet } from "@/lib/audience-magnet.functions";
import { trackCta, trackConversion, trackFunnelStep, track } from "@/lib/analytics";
import { Button } from "../Button";
import type { Audience } from "@/content/audiences";

export function AudienceLeadMagnet({ audience: a }: { audience: Audience }) {
  const submit = useServerFn(requestAudienceMagnet);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const location = `audience_${a.slug}_lead_magnet`;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (state === "loading") return;
    setState("loading");
    trackCta("audience_lead_magnet_submit", {
      location,
      label: a.leadMagnet.title,
      audience_slug: a.slug,
      magnet_slug: a.leadMagnet.slug,
    });
    trackFunnelStep("audience_lead_magnet_request", {
      location,
      audience_slug: a.slug,
      magnet_slug: a.leadMagnet.slug,
    });
    try {
      const sourcePath = typeof window !== "undefined" ? window.location.pathname : null;
      const ua = typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null;
      await submit({
        data: {
          magnet_slug: a.leadMagnet.slug,
          email,
          company: company || null,
          audience_slug: a.slug,
          source_path: sourcePath,
          user_agent: ua,
        },
      });
      setState("done");
      trackConversion({
        conversion_type: "lead_magnet_download",
        location,
        label: a.leadMagnet.title,
        audience_slug: a.slug,
      });
    } catch (err) {
      console.error(err);
      track("audience_lead_magnet_error", { location, audience_slug: a.slug });
      setState("error");
    }
  }

  if (state === "done") {
    return (
      <section className="py-20 md:py-28">
        <div className="container-wide">
          <div className="mx-auto max-w-3xl rounded-3xl border border-mint/40 bg-mint/[0.06] p-8 md:p-10">
            <div className="text-xs uppercase tracking-[0.18em] text-mint">Klart</div>
            <h3 className="mt-2 text-2xl font-semibold md:text-3xl">Tack — vi mejlar materialet inom kort.</h3>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
              Vi skickar {a.leadMagnet.title.toLowerCase()} till {email}. Vill du
              hellre prata direkt? Boka 30 min så går vi igenom det
              tillsammans.
            </p>
            <div className="mt-6">
              <Button
                to="/kontakt"
                variant="mint"
                arrow
                trackId="book_strategy_meeting"
                trackProps={{ location: `${location}_thankyou`, audience_slug: a.slug }}
              >
                Boka strategimöte
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 md:py-28">
      <div className="container-wide">
        <form data-track-form="malgrupp-guide"
          onSubmit={onSubmit}
          className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-surface p-8 md:p-10"
        >
          <div className="text-xs uppercase tracking-[0.18em] text-mint">
            Skräddarsytt för {a.label.toLowerCase()}
          </div>
          <h3 className="mt-2 text-balance text-2xl font-semibold leading-tight md:text-3xl">
            {a.leadMagnet.title}
          </h3>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
            {a.leadMagnet.desc}
          </p>

          <div className="mt-6 grid gap-3 md:grid-cols-2">
            <input
              type="email"
              required
              placeholder="din@mejl.se"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl border border-white/10 bg-background px-4 py-3 text-sm outline-none focus:border-mint"
            />
            <input
              type="text"
              placeholder="Företag (valfritt)"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="rounded-xl border border-white/10 bg-background px-4 py-3 text-sm outline-none focus:border-mint"
            />
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Button type="submit" variant="mint" arrow disabled={state === "loading"}>
              {state === "loading" ? "Skickar…" : a.leadMagnet.cta}
            </Button>
            <span className="text-xs text-muted-foreground">
              Vi mejlar inget om du inte ber oss. Ingen spam, löfte.
            </span>
          </div>

          {state === "error" && (
            <p className="mt-4 text-sm text-peach">
              Något gick fel. Försök igen om en stund eller mejla hey@fenny.se.
            </p>
          )}
        </form>
      </div>
    </section>
  );
}
